import axios, { InternalAxiosRequestConfig } from 'axios';
import { auth } from './firebase';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Legacy exports kept for compatibility — no longer used for token management
// since Firebase handles tokens automatically
export function setAccessToken(_token: string | null): void {
  // No-op: Firebase manages tokens
}

export function getAccessToken(): string | null {
  return null;
}

let activeTenantId: string | null = null;

export function setActiveTenantId(tenantId: string | null): void {
  activeTenantId = tenantId;
}

// Request interceptor: attach Firebase ID token to outgoing requests
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Get the current Firebase user and their ID token
    const currentUser = auth.currentUser;
    if (currentUser && config.headers) {
      try {
        const idToken = await currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch {
        // Token retrieval failed — request will go without auth
      }
    }
    
    // Automatically append tenant_id to /admin/ routes if activeTenantId is set
    if (activeTenantId && config.url?.startsWith('/admin/')) {
      config.params = { ...config.params, tenant_id: activeTenantId };
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 by redirecting to login
// Firebase handles token refresh automatically, so no refresh logic needed
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Force refresh the token and retry once
      const currentUser = auth.currentUser;
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      
      if (currentUser && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await currentUser.getIdToken(true); // force refresh
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        } catch {
          // Token refresh failed — redirect to login
        }
      }
      
      // No user or retry failed — redirect to login
      if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);
